<template>
  <div class="mt-6">
    <header
      class="theme--light v-navigation-drawer elevation-1"
      style="height: 50px; border-radius: 5px; margin-bottom: 10px">
      <div class="v-toolbar__content" style="height: 50px; padding: 25px">
        <div class="v-toolbar__title">
          <h4 class="black--text"><span class="mango-red">Comments on TRAID </span>({{ comments.length }})</h4>
        </div>
      </div>
    </header>

    <v-card>
      <v-card-text class="elevation-1 pb-2">
        <div class="d-flex mb-2" style="gap: 8px">
          <v-select
            v-model="newType"
            :items="commentTypes"
            item-text="label"
            item-value="value"
            label="Comment type"
            hide-details
            outlined
            dense
            :disabled="creating" />
          <v-select
            v-model="newBookingId"
            :items="toraBookings"
            item-text="text"
            item-value="value"
            label="Select booking"
            hide-details
            outlined
            dense
            :disabled="creating" />
        </div>
        <div class="d-flex align-center" style="gap: 8px">
          <v-text-field
            v-model="newComment"
            label="Add a comment on TRAID..."
            hide-details
            solo
            clearable
            :disabled="creating" />
          <v-btn
            class="accent"
            @click="addComment"
            :loading="creating"
            depressed
            :disabled="!newComment || !newBookingId">
            Add
          </v-btn>
        </div>
      </v-card-text>

      <div style="max-height: 250px; overflow-y: auto; background: #f9f9f9">
        <div v-if="loading" class="text-center py-4">
          <v-progress-circular indeterminate color="accent" size="32" />
        </div>

        <div v-else-if="comments.length === 0" class="grey--text text--darken-1 py-4 text-center">No comments</div>

        <v-list v-else dense>
          <v-list-item
            v-for="c in comments"
            :key="c.commentId"
            class="px-3 py-2"
            style="border-bottom: 1px solid #efefef; min-height: unset">
            <v-list-item-content class="py-2">
              <div v-if="editingId !== c.commentId">
                <div class="caption grey--text mb-1">
                  <v-chip x-small label class="mr-2" color="grey darken-2" dark style="font-size: 11px">
                    {{ c.type | formatType }}
                  </v-chip>
                  <v-chip x-small label class="mr-2" color="grey lighten-2" style="font-size: 11px">
                    {{ c.externalBookingCode }}
                  </v-chip>
                  {{ c.lastUpdatedBy || c.createdBy }} &bull; {{ (c.lastUpdatedOn || c.createdOn) | getOnlyDate }}
                  {{ (c.lastUpdatedOn || c.createdOn) | getOnlyTime }} <small>(GMT+{{ gmtDiff }})</small>
                </div>
                <div class="body-2">{{ c.comment }}</div>
              </div>
              <div v-else class="d-flex align-center" style="gap: 8px">
                <v-text-field v-model="editText" dense outlined hide-details autofocus />
                <v-btn small text @click="cancelEdit">Cancel</v-btn>
                <v-btn small color="accent" :loading="saving" @click="saveEdit(c.commentId)">Save</v-btn>
              </div>
            </v-list-item-content>
            <v-list-item-action v-if="editingId !== c.commentId" class="flex-row align-center ma-0" style="gap: 0">
              <v-btn icon @click="startEdit(c)">
                <v-icon size="20" color="third">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon @click="removeComment(c)" :loading="deleting === c.commentId">
                <v-icon size="20" color="primary">mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>
    </v-card>
  </div>
</template>

<script>
import { getErrorMessage } from "@/util/helpers";
import Trainplanet from "@/util/trainplanet.api";
import moment from "moment";

export default {
  name: "TraidComments",
  props: {
    orderId: {
      required: true,
      type: String,
    },
    bookings: {
      required: true,
      type: Array,
    },
  },
  filters: {
    getOnlyDate(val) {
      return val ? moment.utc(val).local().format("YYYY-MM-DD") : "";
    },
    getOnlyTime(val) {
      return val ? moment.utc(val).local().format("HH:mm") : "";
    },
    formatType(val) {
      if (val === "COMMENT_TYPE.BOOKING_COMMENT") return "Booking Comment";
      if (val === "COMMENT_TYPE.BILLING_INFO") return "Billing Info";
      return val;
    },
  },
  data: () => ({
    comments: [],
    loading: false,
    creating: false,
    saving: false,
    deleting: false,
    newComment: "",
    newType: "COMMENT_TYPE.BOOKING_COMMENT",
    newBookingId: null,
    editingId: null,
    editText: "",
    editBookingId: null,
    commentTypes: [
      { label: "Booking Comment", value: "COMMENT_TYPE.BOOKING_COMMENT" },
      { label: "Billing Info", value: "COMMENT_TYPE.BILLING_INFO" },
    ],
  }),
  async mounted() {
    await this.fetchComments();
  },
  computed: {
    gmtDiff() {
      return moment().utcOffset() / 60;
    },
    toraBookings() {
      return (this.bookings || [])
        .filter((b) => b.externalSystem === "tora")
        .map((b) => ({ text: b.externalBookingCode, value: b.id }));
    },
  },
  methods: {
    async fetchComments() {
      this.loading = true;
      try {
        this.comments = await Trainplanet.listTraidComments(this.orderId);
      } catch (e) {
        this.$store.dispatch("error", getErrorMessage(e));
      } finally {
        this.loading = false;
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.newBookingId) return;
      this.creating = true;
      try {
        const created = await Trainplanet.createTraidComment(
          this.orderId,
          this.newBookingId,
          this.newComment.trim(),
          this.newType
        );
        this.comments.unshift(created);
        this.newComment = "";
        this.$store.dispatch("success", "Comment added successfully.");
        this.$emit("comment-changed");
      } catch (e) {
        this.$store.dispatch("error", getErrorMessage(e));
      } finally {
        this.creating = false;
      }
    },
    startEdit(comment) {
      this.editingId = comment.commentId;
      this.editText = comment.comment;
      this.editBookingId = comment.bookingId;
    },
    cancelEdit() {
      this.editingId = null;
      this.editText = "";
      this.editBookingId = null;
    },
    async saveEdit(commentId) {
      if (!this.editText.trim()) return;
      this.saving = true;
      try {
        const updated = await Trainplanet.updateTraidComment(
          this.orderId,
          this.editBookingId,
          commentId,
          this.editText.trim()
        );
        const idx = this.comments.findIndex((c) => c.commentId === commentId);
        if (idx !== -1) this.comments.splice(idx, 1, updated);
        this.editingId = null;
        this.editText = "";
        this.$store.dispatch("success", "Comment updated successfully.");
        this.$emit("comment-changed");
      } catch (e) {
        this.$store.dispatch("error", getErrorMessage(e));
      } finally {
        this.saving = false;
      }
    },
    async removeComment(comment) {
      const confirmed = await this.$confirm(
        "This will delete the comment from TRAID. Are you sure you want to continue?",
        { color: "primary", title: "Delete Comment" }
      );
      if (!confirmed) return;
      this.deleting = comment.commentId;
      try {
        await Trainplanet.deleteTraidComment(this.orderId, comment.bookingId, comment.commentId);
        this.comments = this.comments.filter((c) => c.commentId !== comment.commentId);
        this.$store.dispatch("success", "Comment deleted successfully.");
        this.$emit("comment-changed");
      } catch (e) {
        this.$store.dispatch("error", getErrorMessage(e));
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
